import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'

interface SectionHeadingProps {
    id: string
    title: ReactNode
    accent?: ReactNode
    eyebrow?: string
    intro?: ReactNode
    action?: ReactNode
    align?: 'split' | 'stack' | 'center'
    tone?: 'light' | 'dark'
    className?: string
}

/**
 * The one heading pattern for every content section: an optional eyebrow,
 * a two-part headline (second part in ultramarine), and an optional intro
 * and action. Follow it with content at `mt-12 lg:mt-16`.
 */
export const SectionHeading: FC<SectionHeadingProps> = ({
    id,
    title,
    accent,
    eyebrow,
    intro,
    action,
    align = 'split',
    tone = 'light',
    className
}): ReactNode => {
    const dark = tone === 'dark'

    const heading = (
        <>
            {eyebrow && (
                <p className={cn('eyebrow flex items-center gap-2.5', align === 'center' && 'justify-center', dark ? 'text-white/50' : 'text-stone')}>
                    <span aria-hidden='true' className='size-1.5 rounded-full bg-ultramarine' />
                    {eyebrow}
                </p>
            )}

            <h2
                id={id}
                className={cn(
                    'headline text-[clamp(2.1rem,5vw,4.25rem)] [text-wrap:balance]',
                    eyebrow && 'mt-5',
                    dark ? 'text-white' : 'text-ink'
                )}
            >
                <span className='block'>
                    {title}
                </span>

                {accent && (
                    <span className={cn('block', dark ? 'text-aurora' : 'text-ultramarine')}>
                        {accent}
                    </span>
                )}
            </h2>
        </>
    )

    const support = (intro || action) && (
        <>
            {intro && (
                <p className={cn('text-lg leading-relaxed', dark ? 'text-mist' : 'text-ink-soft')}>
                    {intro}
                </p>
            )}

            {action && (
                <div className={cn(intro && 'mt-7')}>
                    {action}
                </div>
            )}
        </>
    )

    if (align === 'split') {
        return (
            <div className={cn('grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-12', className)}>
                <Reveal className='lg:col-span-7'>
                    {heading}
                </Reveal>

                {support && (
                    <Reveal delay={100} className='max-w-xl lg:col-span-5 lg:pb-2'>
                        {support}
                    </Reveal>
                )}
            </div>
        )
    }

    return (
        <Reveal className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center', className)}>
            {heading}

            {support && (
                <div className={cn('mt-6', align === 'center' && 'mx-auto max-w-xl')}>
                    {support}
                </div>
            )}
        </Reveal>
    )
}
